import React from "react";
import { Sun, Moon } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

interface ThemeToggleProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  variant?: "button" | "switch" | "tab";
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = "",
  size = "md",
  showLabel = false,
  variant = "tab",
}) => {
  const { theme, toggleTheme } = useTheme();

  const sizeClasses = {
    sm: "h-8",
    md: "h-10",
    lg: "h-12",
  };

  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 18,
  };

  // Tab variant - single tab that transitions between themes
  if (variant === "tab") {
    return (
      <div className={`flex items-center ${className}`}>
        <button
          onClick={toggleTheme}
          className={`
            relative inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl
            transition-all duration-300 ease-in-out
            ${sizeClasses[size]}
            ${
              theme === "light"
                ? "bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-yellow-600"
                : "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-600"
            }
            border border-transparent
            group overflow-hidden
          `}
          title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
        >
          {/* Background glow effect */}
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />

          {/* Icon container */}
          <div className="relative flex items-center justify-center">
            <Sun
              size={iconSizes[size]}
              className={`
                absolute transition-all duration-300 ease-in-out transform
                ${
                  theme === "light"
                    ? "rotate-0 scale-100 opacity-100"
                    : "rotate-180 scale-0 opacity-0"
                }
              `}
            />
            <Moon
              size={iconSizes[size]}
              className={`
                absolute transition-all duration-300 ease-in-out transform
                ${
                  theme === "dark"
                    ? "rotate-0 scale-100 opacity-100"
                    : "-rotate-180 scale-0 opacity-0"
                }
              `}
            />
          </div>

          {showLabel && (
            <span className="text-sm font-medium transition-all duration-300">
              {theme === "light" ? "Light" : "Dark"}
            </span>
          )}
        </button>
      </div>
    );
  }

  // Switch variant - iOS/Android-style toggle switch
  if (variant === "switch") {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <button
          onClick={toggleTheme}
          className={`
            relative inline-flex items-center justify-center
            w-14 h-7 rounded-full
            transition-all duration-300 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800
            ${
              theme === "light"
                ? "bg-gradient-to-r from-orange-400 to-yellow-400"
                : "bg-gradient-to-r from-blue-500 to-indigo-600"
            }
            shadow-lg hover:shadow-xl
          `}
          title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
        >
          {/* Toggle circle */}
          <div
            className={`
              absolute w-5 h-5 bg-white rounded-full shadow-lg
              transition-all duration-300 ease-in-out transform
              ${
                theme === "light" ? "translate-x-[-18px]" : "translate-x-[18px]"
              }
            `}
          >
            {/* Icon inside the circle */}
            <div className="absolute inset-0 flex items-center justify-center">
              {theme === "light" ? (
                <Sun size={12} className="text-orange-500" />
              ) : (
                <Moon size={12} className="text-blue-600" />
              )}
            </div>
          </div>
        </button>

        {showLabel && (
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            {theme === "light" ? "Light" : "Dark"}
          </span>
        )}
      </div>
    );
  }

  // Button variant - traditional button with animated icons
  const buttonSizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };

  return (
    <button
      onClick={toggleTheme}
      className={`
        ${buttonSizeClasses[size]}
        relative rounded-xl
        bg-gradient-to-br from-white to-neutral-50 dark:from-neutral-800 dark:to-neutral-900
        border border-neutral-200 dark:border-neutral-700
        hover:from-orange-50 hover:to-orange-100 dark:hover:from-orange-900/20 dark:hover:to-orange-800/20
        hover:border-orange-300 dark:hover:border-orange-600
        shadow-lg hover:shadow-xl
        transition-all duration-300 ease-in-out
        flex items-center justify-center
        group overflow-hidden
        ${className}
      `}
      title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
      aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
    >
      {/* Background glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-400/10 to-yellow-400/10 dark:from-blue-400/10 dark:to-indigo-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />

      {/* Icon container with better positioning */}
      <div className="relative w-full h-full flex items-center justify-center">
        <Sun
          size={iconSizes[size]}
          className={`
            absolute transition-all duration-500 ease-in-out transform
            ${
              theme === "light"
                ? "rotate-0 scale-100 opacity-100 translate-y-0"
                : "rotate-180 scale-0 opacity-0 translate-y-2"
            }
            text-orange-500 drop-shadow-sm
          `}
        />
        <Moon
          size={iconSizes[size]}
          className={`
            absolute transition-all duration-500 ease-in-out transform
            ${
              theme === "dark"
                ? "rotate-0 scale-100 opacity-100 translate-y-0"
                : "-rotate-180 scale-0 opacity-0 -translate-y-2"
            }
            text-blue-500 dark:text-blue-400 drop-shadow-sm
          `}
        />
      </div>

      {/* Subtle indicator ring */}
      <div
        className={`
        absolute inset-0 rounded-xl border-2 transition-all duration-300
        ${
          theme === "light"
            ? "border-orange-200 dark:border-orange-800/30"
            : "border-blue-200 dark:border-blue-800/30"
        }
        opacity-0 group-hover:opacity-100
      `}
      />

      {showLabel && (
        <span className="ml-3 text-sm font-semibold text-neutral-700 dark:text-neutral-200 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-200">
          {theme === "light" ? "Light" : "Dark"}
        </span>
      )}
    </button>
  );
};

export default ThemeToggle;
